// src/services/paymentProviderFactory.js
import { PAYMENT_PROVIDERS } from '../config/payments.js';
import { CloudPaymentsProvider } from './providers/CloudPaymentsProvider.js';
import { MockPaymentProvider } from './providers/MockPaymentProvider.js';

function PaymentProviderFactory() {
    this.providers = new Map();
    this.testMode = process.env.NODE_ENV === 'development' ||
        (import.meta.env && import.meta.env.VITE_PAYMENT_TEST_MODE === 'true');
    this.registerDefaultProviders();
}

PaymentProviderFactory.prototype.registerDefaultProviders = function() {
    this.registerProvider(PAYMENT_PROVIDERS.CLOUD_PAYMENTS, CloudPaymentsProvider);
    this.registerProvider('mock', MockPaymentProvider);
};

PaymentProviderFactory.prototype.registerProvider = function(providerType, ProviderClass) {
    this.providers.set(providerType, ProviderClass);
};

PaymentProviderFactory.prototype.createProvider = function(restaurantSettings, options) {
    if (!restaurantSettings || typeof restaurantSettings !== 'object') {
        console.error('Invalid restaurant settings');
        throw new Error('Invalid restaurant settings');
    }

    console.log('createProvider called', { restaurantSettings, options });

    var provider = restaurantSettings.provider || PAYMENT_PROVIDERS.CLOUD_PAYMENTS;
    var useMock = this.shouldUseMock(restaurantSettings, options || {});
    console.log('🔍 Решение о провайдере:', {
        provider,
        useMock,
        testMode: this.testMode
    });

    if (useMock) {
        console.log('[PaymentFactory] Using mock provider for testing');
        var mergedSettings = Object.assign({}, restaurantSettings, {
            testScenario: (options && options.testScenario) || 'SUCCESS'
        });
        return new MockPaymentProvider(mergedSettings);
    }

    console.log('Используем реального провайдера:', provider);
    var ProviderClass = this.providers.get(provider);

    if (!ProviderClass) {
        console.error('Provider not supported:', provider);
        throw new Error('Payment provider "' + provider + '" is not supported');
    }

    return new ProviderClass(restaurantSettings);
};

PaymentProviderFactory.prototype.shouldUseMock = function(settings, options) {
    if (options.forceMock === true) {
        return true;
    }

    if (options.forceReal === true) {
        return false;
    }

    if (this.testMode && options.allowMock !== false) {
        return true;
    }

    if (!settings.credentials || typeof settings.credentials !== 'object') {
        return true;
    }

    var publicKey = settings.credentials.publicKey;
    if (!publicKey ||
        typeof publicKey !== 'string' ||
        publicKey.includes('test') ||
        publicKey.includes('mock') ||
        publicKey.length < 10) {
        return true;
    }

    if (settings.testMode === true) {
        return true;
    }

    return false;
};

PaymentProviderFactory.prototype.createTestProvider = function(scenario) {
    scenario = scenario || 'SUCCESS';

    return new MockPaymentProvider({
        provider: 'mock',
        testMode: true,
        testScenario: scenario,
        credentials: {
            publicKey: 'pk_test_mock_key',
            apiSecret: 'mock_secret'
        }
    });
};

PaymentProviderFactory.prototype.getRegisteredProviders = function() {
    return Array.from(this.providers.keys());
};

PaymentProviderFactory.prototype.getWidgetForProvider = function(providerType) {
    const ProviderClass = this.providers.get(providerType);
    if (!ProviderClass) return null;

    // Создаем временный экземпляр для получения виджета
    const tempInstance = new ProviderClass({});
    return tempInstance.getWidget();
};

PaymentProviderFactory.prototype.getAvailableWidgets = function() {
    const widgets = [];
    for (const [providerType, ProviderClass] of this.providers) {
        const tempInstance = new ProviderClass({});
        const widget = tempInstance.getWidget();
        if (widget) {
            widgets.push({
                provider: providerType,
                widget: widget,
                name: tempInstance.getWidgetName()
            });
        }
    }
    return widgets;
};

var paymentProviderFactory = new PaymentProviderFactory();

export { PaymentProviderFactory, paymentProviderFactory };